import { useParams, Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft } from "lucide-react";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { KPIRow } from "@/components/v2/kpi-row";
import { Button } from "@/components/ui/button";
import { useDemoCatalogEntry } from "@/hooks/use-demo-catalog";
import { RunFullPipelineButton } from "./_run-full-pipeline-button";
import { buildDemoCatalogKpis } from "./_demo-catalog-kpis";

type EntityRow = { entity: string; count: number };

const ENTITY_COLUMNS: ColumnDef<EntityRow, unknown>[] = [
  {
    accessorKey: "entity",
    header: "Entity",
    cell: ({ row }) => (
      <span className="font-medium">{row.original.entity}</span>
    ),
  },
  {
    accessorKey: "count",
    header: "Records",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="typo-mono">{row.original.count.toLocaleString()}</span>
    ),
  },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function V2SettingsDemoCatalogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: entry, isLoading, isError, refetch } = useDemoCatalogEntry(id);

  const kpis = entry ? buildDemoCatalogKpis(entry) : [];

  const entityRows: EntityRow[] = entry
    ? Object.entries(entry.record_counts as Record<string, number>)
        .map(([entity, count]) => ({ entity, count: count as number }))
        .sort((a, b) => b.count - a.count)
    : [];

  const metaItems = entry
    ? [
        { label: "Company", value: entry.company },
        { label: "Business", value: entry.business },
        { label: "Industry", value: entry.industry },
        { label: "Scale mode", value: entry.scale_mode },
        { label: "Size", value: entry.size_label },
        { label: "Generator version", value: entry.generator_version },
        { label: "Generated", value: formatDate(entry.generated_at) },
      ]
    : [];

  return (
    <ListPage
      title={entry?.display_name ?? "Demo Dataset"}
      breadcrumbs={[
        { label: "Settings" },
        { label: "Demo Catalog", href: "/settings/demo-import" },
        { label: entry?.display_name ?? id ?? "Detail" },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings/demo-import">
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back to catalog
            </Link>
          </Button>
          <RunFullPipelineButton datasetId={id} />
        </div>
      }
    >
      {!isLoading && !isError && entry && (
        <>
          <KPIRow cards={kpis} />

          {metaItems.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 typo-body text-sm">
              {metaItems.map(({ label, value }) => (
                <span key={label}>
                  <span className="text-[color:var(--color-text-lo)]">
                    {label}:
                  </span>{" "}
                  <span className="text-[color:var(--color-text-hi)]">
                    {value}
                  </span>
                </span>
              ))}
            </div>
          )}

          <div className="mt-6">
            <DataTable
              columns={ENTITY_COLUMNS}
              data={entityRows}
              total={entityRows.length}
              emptyMessage="No entity counts"
              emptyDescription="This dataset has no entity record counts."
            />
          </div>
        </>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <span className="typo-body text-[color:var(--color-text-lo)]">
            Loading dataset…
          </span>
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-3 py-12">
          <p className="typo-body text-[color:var(--color-danger)]">
            Failed to load dataset.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}
    </ListPage>
  );
}
